/**
 * Monthly Expenses Page
 * 
 * Management of monthly expenses for the current budget period.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus,
  Search,
  Receipt,
  ArrowUpDown,
  Loader2,
  Trash2,
  X,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { DateInput } from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useGatedTypeSelect } from '@/hooks/use-gated-type-select';
import { EXPENSE_TYPE_FEATURE, LOCKED_EXPENSE_TYPE_MESSAGE } from '@/config/features';
import {
  PeriodNavigator,
  TransactionCard,
  FinancialSummaryCards,
  CurrencyDisplay,
  BulkSelectionSummary,
} from '@/components/financial';
import {
  useMonthlyExpenses,
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useOverdueExpenses,
  useUpcomingExpenses,
  useCreateMonthlyExpense,
  useUpdateMonthlyExpense,
  useDeleteMonthlyExpense,
  useRealizeMonthlyExpense,
  useUndoMonthlyExpenseRealization,
  useFinancialList,
  useAgents,
  useFinancialSettings,
} from '@/hooks/use-financial';
import { format } from 'date-fns';
import {
  getBudgetPeriodPermissions,
  isDateWithinPeriod,
  getDefaultRealizationDate,
  formatDatePtBR,
} from '@/lib/date';
import { toast } from '@/hooks/use-toast';
import {
  FIRST_ACCESS_TUTORIAL_DEMO,
  FIRST_ACCESS_TUTORIAL_STEPS,
  getTutorialDateInBudgetPeriod,
  markTutorialKnownItemIds,
  findTutorialCreatedItem,
  requestTutorialAdvance,
  useActiveOnboardingStep,
} from '@/components/onboarding/tutorial-runtime';

const TUTORIAL_SCOPE = 'monthly-expenses';

// Realce do botão de criação quando o formulário está completo.
const CREATE_READY_BUTTON_CLASS =
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600';
import { normalizeSearchText } from '@/lib/text';
import type { MonthlyExpense, ExpenseType } from '@/types/financial';
import { DueEmptyState, DueFilterTabsList } from '@/components/financial/DueFilterTabs';
import { UPCOMING_DAYS } from '@/lib/dueWindow';

const expenseTypeLabels: Record<ExpenseType, string> = {
  continuous: 'Contínua',
  single: 'Única',
  installment: 'Parcelada',
};

// Despesa parcelada guarda em planned_amount o valor MENSAL da parcela (não o
// total da série, ao contrário das receitas) — exibir sem dividir.
const getExpensePlannedAmountForPO = (expense: MonthlyExpense): number =>
  Number(expense.planned_amount || 0);

export function MonthlyExpensesPage() {
  const appliedTutorialStepRef = useRef<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditScopeDialogOpen, setIsEditScopeDialogOpen] = useState(false);
  const [editScope, setEditScope] = useState<'current' | 'future'>('current');
  const [isTypeChangeDialogOpen, setIsTypeChangeDialogOpen] = useState(false);
  const [isRealizeDialogOpen, setIsRealizeDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<MonthlyExpense | null>(null);
  const [expenseToEdit, setExpenseToEdit] = useState<MonthlyExpense | null>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<MonthlyExpense | null>(null);
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [isDeleteScopeDialogOpen, setIsDeleteScopeDialogOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState<'current' | 'future'>('current');
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Gating por tipo: Contínua/Parcelada exigem feature do plano; Única é livre.
  const expenseTypeGate = useGatedTypeSelect(EXPENSE_TYPE_FEATURE, LOCKED_EXPENSE_TYPE_MESSAGE);
  // Default seguro: se Contínua estiver bloqueada, abre o form em Única.
  const defaultExpenseType: ExpenseType = expenseTypeGate.isLocked('continuous')
    ? 'single'
    : 'continuous';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    expense_type: 'single' as ExpenseType,
    planned_amount: '',
    category: '',
    due_date: '',
    notes: '',
    total_installments: '',
    remaining_installments: '',
    agent_id: '',
  });
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    expense_type: 'continuous' as ExpenseType,
    planned_amount: '',
    category: '',
    due_date: '',
    notes: '',
    total_installments: '',
    remaining_installments: '',
    agent_id: '',
  });
  
  const [realizeFormData, setRealizeFormData] = useState({
    realized_amount: '',
    realized_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    notes: '',
    agent_id: '',
  });

  // Validation helpers for installment fields
  const getInstallmentValidationErrors = (
    total: string,
    remaining: string
  ): { totalError: string | null; remainingError: string | null } => {
    const totalNum = Number.parseInt(total, 10);
    const remainingNum = Number.parseInt(remaining, 10);
    let totalError: string | null = null;
    let remainingError: string | null = null;

    if (Number.isFinite(totalNum) && totalNum === 1) {
      totalError = 'Trocar o tipo de despesa para Única';
    }

    if (Number.isFinite(totalNum) && Number.isFinite(remainingNum) && remainingNum > totalNum) {
      remainingError = 'O número de parcelas restantes não pode ser maior que o total de parcelas.';
    }

    return { totalError, remainingError };
  };

  const createFormInstallmentErrors =
    formData.expense_type === 'installment'
      ? getInstallmentValidationErrors(formData.total_installments, formData.remaining_installments)
      : { totalError: null, remainingError: null };
  const createFormTotalInstallmentError = createFormInstallmentErrors.totalError;
  const createFormRemainingInstallmentError = createFormInstallmentErrors.remainingError;

  const editFormInstallmentErrors =
    editFormData.expense_type === 'installment'
      ? getInstallmentValidationErrors(editFormData.total_installments, editFormData.remaining_installments)
      : { totalError: null, remainingError: null };
  const editFormTotalInstallmentError = editFormInstallmentErrors.totalError;
  const editFormRemainingInstallmentError = editFormInstallmentErrors.remainingError;

  // Edit realized dialog
  const [isEditRealizedDialogOpen, setIsEditRealizedDialogOpen] = useState(false);
  const [isUndoRealizedDialogOpen, setIsUndoRealizedDialogOpen] = useState(false);
  const [editRealizedFormData, setEditRealizedFormData] = useState({
    realized_amount: '',
    realized_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    agent_id: '',
  });

  const { data: currentPeriod, isLoading: isPeriodLoading } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();
  const { data: expenses, isLoading: isExpensesLoading } = useMonthlyExpenses(currentPeriod?.id);
  const { data: overdueExpenses } = useOverdueExpenses(currentPeriod?.id);
  const { data: upcomingExpenses } = useUpcomingExpenses(UPCOMING_DAYS, currentPeriod?.id);
  const { data: categoriesList } = useFinancialList('expense_categories');
  const { data: paymentMethodsList } = useFinancialList('payment_methods');
  const { data: expenseNamesList } = useFinancialList('monthly_expense_names');
  const { data: agents } = useAgents();
  const expenseAgents = useMemo(() => agents || [], [agents]);
  const payerAgents = useMemo(() => {
    const allAgents = agents || [];
    const preferredAgents = allAgents.filter(
      (agent) => agent.is_default_payer || agent.is_default_agent
    );
    return preferredAgents.length > 0 ? preferredAgents : allAgents;
  }, [agents]);
  const defaultExpenseAgentId =
    expenseAgents.find((agent) => agent.is_default_agent)?.id ||
    expenseAgents[0]?.id ||
    '';
  const defaultPayerAgentId =
    payerAgents.find((agent) => agent.is_default_agent)?.id ||
    payerAgents.find((agent) => agent.is_default_payer)?.id ||
    payerAgents[0]?.id ||
    defaultExpenseAgentId;
  const { data: financialSettings } = useFinancialSettings();
  const activeTutorialStep = useActiveOnboardingStep();
  const { mutate: createExpense, isPending: isCreating } = useCreateMonthlyExpense();
  const { mutate: updateExpense, isPending: isUpdating } = useUpdateMonthlyExpense();
  const { mutate: deleteExpense, mutateAsync: deleteExpenseAsync, isPending: isDeletingExpense } =
    useDeleteMonthlyExpense();
  const { mutate: realizeExpense, isPending: isRealizing } = useRealizeMonthlyExpense();
  const { mutate: undoExpenseRealization, isPending: isUndoingRealization } =
    useUndoMonthlyExpenseRealization();

  const periodPermissions = getBudgetPeriodPermissions({
    startDate: currentPeriod?.start_date,
    endDate: currentPeriod?.end_date,
    paymentDay: financialSettings?.default_payment_day ?? currentPeriod?.payment_day,
  });
  const canPlanInPeriod = periodPermissions.canPlan;
  const canRealizeInPeriod = periodPermissions.canRealize;
  const tutorialDateInPeriod = getTutorialDateInBudgetPeriod(currentPeriod);
  const tutorialCategory = categoriesList?.items?.[0]?.name || '';
  const tutorialPaymentMethod = paymentMethodsList?.items?.[0]?.name || '';
  const tutorialExpense = useMemo(
    () => findTutorialCreatedItem(TUTORIAL_SCOPE, expenses),
    [expenses]
  );
  
  // Reset form when dialog closes
  /** Formulário completo: habilita o envio e recebe o realce verde. */
  const isCreateFormComplete =
    Boolean(formData.name) &&
    Boolean(formData.planned_amount) &&
    Boolean(formData.due_date) &&
    Boolean(formData.category) &&
    Boolean(formData.agent_id) &&
    (formData.expense_type !== 'installment' ||
      Boolean(formData.remaining_installments && formData.total_installments)) &&
    !createFormTotalInstallmentError &&
    !createFormRemainingInstallmentError;

  useEffect(() => {
    if (!isCreateDialogOpen) {
      setFormData({
        name: '',
        expense_type: defaultExpenseType,
        planned_amount: '',
        category: '',
        due_date: '',
        notes: '',
        total_installments: '',
        remaining_installments: '',
        agent_id: '',
      });
    }
  }, [isCreateDialogOpen, defaultExpenseType]);

  // Ao abrir o modal de criação, pré-preenche o agente da despesa (priorizando Agente Padrão)
  useEffect(() => {
    if (!isCreateDialogOpen) return;

    const defaultAgentId = defaultExpenseAgentId;
    if (!defaultAgentId) return;

    setFormData((prev) => (prev.agent_id ? prev : { ...prev, agent_id: defaultAgentId }));
  }, [defaultExpenseAgentId, isCreateDialogOpen]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.monthlySave;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!canPlanInPeriod || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    // Fotografa o que já existe: o registro que sobrar depois é o lançamento
    // real que o usuário acabou de criar no tutorial.
    markTutorialKnownItemIds(TUTORIAL_SCOPE, (expenses || []).map((expense) => expense.id));
    setIsCreateDialogOpen(true);
    setFormData({
      // Nome vazio de propósito: o usuário escolhe, na listagem existente,
      // uma despesa verdadeira dele.
      name: '',
      expense_type: defaultExpenseType,
      planned_amount: String(FIRST_ACCESS_TUTORIAL_DEMO.monthlyExpense.amount),
      category: tutorialCategory,
      due_date: tutorialDateInPeriod,
      notes: '',
      total_installments: '',
      remaining_installments: '',
      agent_id: defaultExpenseAgentId,
    });
  }, [
    activeTutorialStep,
    canPlanInPeriod,
    defaultExpenseAgentId,
    tutorialCategory,
    tutorialDateInPeriod,
    defaultExpenseType,
    expenses,
  ]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.monthlyConfirm;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!tutorialExpense || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    setSelectedExpense(tutorialExpense);
    setRealizeFormData({
      realized_amount: String(tutorialExpense.planned_amount || FIRST_ACCESS_TUTORIAL_DEMO.monthlyExpense.amount),
      realized_date: tutorialExpense.due_date || tutorialDateInPeriod,
      payment_method: tutorialExpense.payment_method || tutorialPaymentMethod,
      notes: '',
      agent_id: tutorialExpense.payer_agent_id || defaultPayerAgentId || '',
    });
    setIsRealizeDialogOpen(true);
  }, [
    activeTutorialStep,
    defaultPayerAgentId,
    tutorialDateInPeriod,
    tutorialExpense,
    tutorialPaymentMethod,
  ]);

  const handleCreateExpense = () => {
    if (!formData.name) return;
    if (!formData.expense_type) return;
    if (!formData.planned_amount) return;
    if (!formData.due_date) return;
    if (!formData.category) return;
    if (!formData.agent_id) return;
    if (!currentPeriod?.id) return;

    const isInstallment = formData.expense_type === 'installment';
    let totalInstallments: number | undefined;
    let remainingInstallments: number | undefined;

    if (isInstallment) {
      if (!formData.total_installments || !formData.remaining_installments) return;

      totalInstallments = Number.parseInt(formData.total_installments, 10);
      remainingInstallments = Number.parseInt(formData.remaining_installments, 10);

      if (Number.isFinite(totalInstallments) && totalInstallments === 1) {
        return;
      }

      // A validação de parcelas restantes > total é feita inline no formulário
      if (
        Number.isFinite(totalInstallments) &&
        Number.isFinite(remainingInstallments) &&
        remainingInstallments > totalInstallments
      ) {
        return;
      }
    }

    createExpense({
      data: {
        name: formData.name,
        expense_type: formData.expense_type,
        planned_amount: Number.parseFloat(formData.planned_amount),
        category: formData.category || undefined,
        due_date: formData.due_date || undefined,
        notes: formData.notes || undefined,
        current_installment:
          isInstallment && totalInstallments && remainingInstallments
            ? totalInstallments - remainingInstallments + 1
            : undefined,
        total_installments: isInstallment && totalInstallments ? totalInstallments : undefined,
        // Regra: para despesas, o "Agente" corresponde ao Pagador (payer_agent_id).
        payer_agent_id: formData.agent_id || undefined,
      },
      periodId: currentPeriod.id,
    }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.monthlySave);
        setIsCreateDialogOpen(false);
        // Reset filtro para "Todas" para que o usuário veja a nova despesa criada
        setActiveTab('all');
        // Reset form
        setFormData({
          name: '',
          expense_type: defaultExpenseType,
          planned_amount: '',
          category: '',
          due_date: '',
          notes: '',
          total_installments: '',
          remaining_installments: '',
          agent_id: '',
        });
      }
    });
  };

  // Valida que a data de pagamento pertence ao período do lançamento e, caso
  // contrário, notifica o usuário. Espelha a regra do backend (fonte de verdade).
  const validateRealizationDate = (dateStr?: string): boolean => {
    if (isDateWithinPeriod(dateStr, currentPeriod?.start_date, currentPeriod?.end_date)) {
      return true;
    }
    toast({
      title: 'Data fora do período orçamentário',
      description: `A data de pagamento deve estar dentro do período do lançamento (${formatDatePtBR(
        currentPeriod?.start_date
      )} a ${formatDatePtBR(currentPeriod?.end_date)}).`,
      variant: 'destructive',
    });
    return false;
  };

  const handleOpenRealizeDialog = (expense: MonthlyExpense) => {
    setSelectedExpense(expense);
    const defaultPayerId = defaultPayerAgentId;
    setRealizeFormData({
      realized_amount: expense.planned_amount?.toString() || '',
      // Data padrão = hoje quando hoje pertence ao período; senão, clamp para o
      // limite do PO (evita pré-preencher com data fora de um PO já encerrado).
      realized_date: getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
      payment_method: expense.payment_method || '',
      notes: '',
      // "Quem pagou": pré-preenche com quem já pagou (se houver), senão o agente
      // da despesa, senão o pagador padrão.
      agent_id: expense.paid_by_agent_id || expense.payer_agent_id || defaultPayerId || '',
    });
    setIsRealizeDialogOpen(true);
  };

  const handleRealizeExpense = () => {
    if (!selectedExpense) return;
    if (!realizeFormData.realized_amount) return;
    if (!realizeFormData.realized_date) return;
    if (!realizeFormData.agent_id) return;
    if (!realizeFormData.payment_method) return;
    if (!validateRealizationDate(realizeFormData.realized_date)) return;

    realizeExpense({
      id: selectedExpense.id,
      data: {
        realized_amount: Number.parseFloat(realizeFormData.realized_amount),
        realized_date: realizeFormData.realized_date || undefined,
        payment_method: realizeFormData.payment_method || undefined,
        notes: realizeFormData.notes || undefined,
        agent_id: realizeFormData.agent_id || undefined,
      }
    }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.monthlyConfirm);
        setIsRealizeDialogOpen(false);
        setSelectedExpense(null);
      }
    });
  };

  const handleOpenEditRealizedDialog = (expense: MonthlyExpense) => {
    setSelectedExpense(expense);
    setEditRealizedFormData({
      realized_amount: String(expense.realized_amount || ''),
      realized_date:
        expense.realized_date ||
        getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
      payment_method: expense.payment_method || '',
      // "Quem pagou": pré-preenche com quem já pagou (se houver), senão o agente
      // da despesa, senão o pagador padrão.
      agent_id: expense.paid_by_agent_id || expense.payer_agent_id || defaultPayerAgentId || '',
    });
    setIsEditRealizedDialogOpen(true);
  };

  const handleUpdateRealized = () => {
    if (!selectedExpense || !editRealizedFormData.realized_amount) return;
    if (!validateRealizationDate(editRealizedFormData.realized_date)) return;

    updateExpense({
      id: selectedExpense.id,
      data: {
        realized_amount: Number.parseFloat(editRealizedFormData.realized_amount),
        realized_date: editRealizedFormData.realized_date || undefined,
        payment_method: editRealizedFormData.payment_method || undefined,
        // "Quem pagou" é gravado separado; nunca sobrescreve o "Agente da despesa".
        paid_by_agent_id: editRealizedFormData.agent_id || undefined,
      }
    }, {
      onSuccess: () => {
        setIsEditRealizedDialogOpen(false);
        setSelectedExpense(null);
      }
    });
  };

  const handleConfirmUndoRealized = () => {
    if (!selectedExpense) return;

    undoExpenseRealization(selectedExpense.id, {
      onSuccess: () => {
        setIsUndoRealizedDialogOpen(false);
        setIsEditRealizedDialogOpen(false);
        setSelectedExpense(null);
      },
    });
  };

  const handleOpenEditDialog = (expense: MonthlyExpense) => {
    setExpenseToEdit(expense);
    const defaultAgentId = defaultExpenseAgentId;
    setEditFormData({
      name: expense.name,
      expense_type: expense.expense_type,
      planned_amount: String(expense.planned_amount),
      category: expense.category || '',
      due_date: expense.due_date || '',
      notes: expense.notes || '',
      total_installments: expense.total_installments ? String(expense.total_installments) : '',
      remaining_installments:
        expense.total_installments && expense.current_installment
          ? String(expense.total_installments - expense.current_installment + 1)
          : '',
      agent_id: expense.payer_agent_id || defaultAgentId || '',
    });
    setEditScope('current'); // Reset ao abrir o modal
    setIsEditScopeDialogOpen(false);
    setIsEditDialogOpen(true);
  };

  const submitUpdateExpense = (applyToFuture: boolean) => {
    if (!expenseToEdit) return;
    if (!editFormData.name) return;
    if (!editFormData.expense_type) return;
    if (!editFormData.planned_amount) return;
    if (!editFormData.due_date) return;
    if (!editFormData.category) return;
    if (!editFormData.agent_id) return;

    const isInstallment = editFormData.expense_type === 'installment';
    let totalInstallments: number | undefined;
    let remainingInstallments: number | undefined;

    if (isInstallment) {
      if (!editFormData.total_installments || !editFormData.remaining_installments) return;

      totalInstallments = Number.parseInt(editFormData.total_installments, 10);
      remainingInstallments = Number.parseInt(editFormData.remaining_installments, 10);

      if (Number.isFinite(totalInstallments) && totalInstallments === 1) {
        return;
      }

      // A validação de parcelas restantes > total é feita inline no formulário
      if (
        Number.isFinite(totalInstallments) &&
        Number.isFinite(remainingInstallments) &&
        remainingInstallments > totalInstallments
      ) {
        return;
      }
    }

    updateExpense(
      {
        id: expenseToEdit.id,
        data: {
          name: editFormData.name,
          expense_type: editFormData.expense_type,
          planned_amount: Number.parseFloat(editFormData.planned_amount),
          category: editFormData.category || undefined,
          due_date: editFormData.due_date || undefined,
          notes: editFormData.notes || undefined,
          current_installment:
            isInstallment && totalInstallments && remainingInstallments
              ? totalInstallments - remainingInstallments + 1
              : undefined,
          total_installments: isInstallment && totalInstallments ? totalInstallments : undefined,
          // Regra: para despesas, o "Agente" corresponde ao Pagador (payer_agent_id).
          payer_agent_id: editFormData.agent_id || undefined,
          apply_to_future: applyToFuture,
        },
      },
      {
        onSuccess: () => {
          setIsEditScopeDialogOpen(false);
          setIsTypeChangeDialogOpen(false);
          setIsEditDialogOpen(false);
          setExpenseToEdit(null);
        },
      }
    );
  };

  const handleAttemptUpdateExpense = () => {
    if (!expenseToEdit) return;

    const typeChanged = expenseToEdit.expense_type !== editFormData.expense_type;

    if (typeChanged) {
      setIsTypeChangeDialogOpen(true);
      return;
    }

    const needsScopeConfirm =
      expenseToEdit.expense_type === 'continuous' ||
      expenseToEdit.expense_type === 'installment' ||
      editFormData.expense_type === 'continuous' ||
      editFormData.expense_type === 'installment';

    if (needsScopeConfirm) {
      setEditScope('current');
      setIsEditScopeDialogOpen(true);
      return;
    }

    submitUpdateExpense(false);
  };

  const handleConfirmEditScope = () => {
    submitUpdateExpense(editScope === 'future');
  };

  const handleConfirmTypeChange = () => {
    submitUpdateExpense(true);
  };

  const handleOpenDeleteDialog = (expense: MonthlyExpense) => {
    setExpenseToDelete(expense);
    setDeleteScope('current');

    if (expense.expense_type === 'continuous' || expense.expense_type === 'installment') {
      setIsDeleteConfirmDialogOpen(false);
      setIsDeleteScopeDialogOpen(true);
      return;
    }

    setIsDeleteScopeDialogOpen(false);
    setIsDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDelete = (applyToFuture: boolean) => {
    if (!expenseToDelete) return;

    deleteExpense(
      { id: expenseToDelete.id, apply_to_future: applyToFuture },
      {
        onSuccess: () => {
          setIsDeleteConfirmDialogOpen(false);
          setIsDeleteScopeDialogOpen(false);
          setExpenseToDelete(null);
        },
      }
    );
  };

  const handleToggleExpenseSelection = (expenseId: string, checked: boolean) => {
    setSelectedExpenseIds((prev) => {
      if (checked) {
        return prev.includes(expenseId) ? prev : [...prev, expenseId];
      }
      return prev.filter((id) => id !== expenseId);
    });
  };

  const handleToggleAllExpenses = (checked: boolean) => {
    if (checked) {
      setSelectedExpenseIds(visibleExpenseIds);
      return;
    }
    setSelectedExpenseIds([]);
  };

  const handleBulkDeleteExpenses = async () => {
    if (selectedVisibleExpenseIds.length === 0) return;

    const idsToDelete = [...selectedVisibleExpenseIds];
    setIsBulkDeleting(true);

    const results = await Promise.allSettled(
      idsToDelete.map((id) => deleteExpenseAsync({ id, apply_to_future: true }))
    );

    const failedIds = idsToDelete.filter((_, index) => results[index]?.status === 'rejected');

    setIsBulkDeleting(false);

    if (failedIds.length === 0) {
      setSelectedExpenseIds([]);
      setIsBulkDeleteDialogOpen(false);
      return;
    }

    setSelectedExpenseIds(failedIds);
  };

  const isLoading = isPeriodLoading || isExpensesLoading;

  // Get displayed expenses based on active tab
  const getDisplayedExpenses = () => {
    switch (activeTab) {
      case 'overdue':
        return overdueExpenses || [];
      case 'upcoming':
        return upcomingExpenses || [];
      default:
        return expenses || [];
    }
  };

  // Filter expenses
  const filteredExpenses = getDisplayedExpenses().filter((expense) => {
    const matchesSearch = normalizeSearchText(expense.name).includes(normalizeSearchText(searchTerm));
    const matchesStatus = statusFilter === 'all' || expense.status === statusFilter;
    const matchesType = typeFilter === 'all' || expense.expense_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sort expenses
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
        break;
      case 'date':
        comparison = (a.due_date || a.created_at) > (b.due_date || b.created_at) ? 1 : -1;
        break;
      case 'value':
        comparison = Number(a.planned_amount) - Number(b.planned_amount);
        break;
      case 'type':
        comparison = (a.expense_type || '').localeCompare(b.expense_type || '');
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const visibleExpenseIds = sortedExpenses.map((expense) => expense.id);
  const selectedVisibleExpenseIds = selectedExpenseIds.filter((id) => visibleExpenseIds.includes(id));
  const selectedExpenseCount = selectedVisibleExpenseIds.length;
  const allExpensesSelected =
    visibleExpenseIds.length > 0 && selectedExpenseCount === visibleExpenseIds.length;
  const someExpensesSelected = selectedExpenseCount > 0 && !allExpensesSelected;
  const selectedExpenses = sortedExpenses.filter((expense) =>
    selectedVisibleExpenseIds.includes(expense.id)
  );
  const selectedPlannedTotal = selectedExpenses.reduce(
    // Parceladas: soma o valor da parcela do período, não o total parcelado.
    (sum, expense) => sum + getExpensePlannedAmountForPO(expense),
    0
  );
  const selectedRealizedTotal = selectedExpenses.reduce(
    (sum, expense) => sum + Number(expense.realized_amount || 0),
    0
  );

  useEffect(() => {
    setSelectedExpenseIds((prev) => {
      const next = prev.filter((id) => visibleExpenseIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleExpenseIds]);

  // Calculate stats (convert from string/Decimal to number)
  const overdueCount = overdueExpenses?.length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando despesas mensais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Despesas Mensais</h1>
          <p className="text-muted-foreground">
            Gerencie suas <span className="font-semibold">obrigações</span> mensais
          </p>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <PeriodNavigator
            currentPeriod={currentPeriod}
            periods={navigation?.periods || []}
            isLoading={isNavigating}
            onNavigate={navigatePeriod}
            variant="boxed"
          />
        </div>
      </div>

      {/* Financial Summary Cards - sempre o 1º bloco de KPIs e fixo ao rolar */}
      <FinancialSummaryCards periodId={currentPeriod?.id} />

      {/* Tabs and Filters */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <DueFilterTabsList
          allCount={expenses?.length || 0}
          overdueCount={overdueCount}
          upcomingCount={upcomingExpenses?.length || 0}
          overdueFeature="despesas-vencidas"
        />

        {/* Filters */}
        <Card id="sheet">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              {/* Primary actions row */}
              <div className="flex gap-2 sm:gap-4">
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="gap-2 flex-1 sm:flex-none"
                  disabled={!canPlanInPeriod}
                  data-tour-id="create-monthly-expense"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Nova Despesa</span>
                  <span className="sm:hidden">Nova</span>
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar despesas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearchTerm('')}
                    aria-label="Limpar busca de despesas mensais"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {/* Filters row */}
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="realized">Realizado</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="continuous">Contínua</SelectItem>
                    <SelectItem value="single">Única</SelectItem>
                    <SelectItem value="installment">Parcelada</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Nome (A-Z)</SelectItem>
                    <SelectItem value="date">Vencimento</SelectItem>
                    <SelectItem value="value">Valor</SelectItem>
                    <SelectItem value="type">Tipo</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="h-10 w-10 shrink-0"
                  title={sortDirection === 'asc' ? 'Ordenação crescente' : 'Ordenação decrescente'}
                >
                  <ArrowUpDown className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {canPlanInPeriod && sortedExpenses.length > 0 && (
          <Card>
            <CardContent className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all-monthly-expenses"
                  checked={allExpensesSelected ? true : someExpensesSelected ? 'indeterminate' : false}
                  onCheckedChange={(checked) => handleToggleAllExpenses(checked === true)}
                  aria-label="Selecionar todas as despesas mensais"
                />
                <Label htmlFor="select-all-monthly-expenses" className="cursor-pointer">
                  Selecionar Todos
                </Label>
              </div>
              {selectedExpenseCount > 0 && (
                <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                  <BulkSelectionSummary
                    plannedLabel="Planejado"
                    plannedTotal={selectedPlannedTotal}
                    realizedLabel="Realizado"
                    realizedTotal={selectedRealizedTotal}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="gap-2 w-full sm:w-auto"
                    onClick={() => setIsBulkDeleteDialogOpen(true)}
                    disabled={isBulkDeleting || isDeletingExpense}
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir ({selectedExpenseCount})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expenses Lists */}
        <TabsContent value="all" className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
          {renderExpensesList(
            sortedExpenses,
            handleOpenDeleteDialog,
            handleOpenRealizeDialog,
            handleOpenEditDialog,
            handleOpenEditRealizedDialog,
            () => setIsCreateDialogOpen(true),
            canPlanInPeriod,
            canRealizeInPeriod,
            selectedExpenseIds,
            handleToggleExpenseSelection
          )}
        </TabsContent>

        <TabsContent value="overdue" className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
          {overdueCount === 0 ? (
            <DueEmptyState
              title="Nenhuma despesa vencida!"
              message="Todas as despesas estão em dia 🎉"
              backLabel="Voltar para todas as despesas"
              onBack={() => setActiveTab('all')}
            />
          ) : (
            renderExpensesList(
              sortedExpenses,
              handleOpenDeleteDialog,
              handleOpenRealizeDialog,
              handleOpenEditDialog,
              handleOpenEditRealizedDialog,
              () => setIsCreateDialogOpen(true),
              canPlanInPeriod,
              canRealizeInPeriod,
              selectedExpenseIds,
              handleToggleExpenseSelection
            )
          )}
        </TabsContent>

        <TabsContent value="upcoming" className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
          {(upcomingExpenses?.length || 0) === 0 ? (
            <DueEmptyState
              title={`Nenhuma despesa nos próximos ${UPCOMING_DAYS} dias!`}
              message="Todas as despesas estão em dia 🎉"
              backLabel="Voltar para todas as despesas"
              onBack={() => setActiveTab('all')}
            />
          ) : (
            renderExpensesList(
              sortedExpenses,
              handleOpenDeleteDialog,
              handleOpenRealizeDialog,
              handleOpenEditDialog,
              handleOpenEditRealizedDialog,
              () => setIsCreateDialogOpen(true),
              canPlanInPeriod,
              canRealizeInPeriod,
              selectedExpenseIds,
              handleToggleExpenseSelection
            )
          )}
        </TabsContent>
      </Tabs>

      {/* Create Expense Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-tour-id="monthly-create-dialog" className="sm:max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Despesa Mensal</DialogTitle>
            <DialogDescription>
              São as obrigações mensais relacionadas a uma data de vencimento - aluguel,
              escola, energia etc. Veja a listagem.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            {formData.expense_type === 'installment' ? (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">
                      Nome da despesa <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      id="name"
                      mode="input"
                      value={formData.name}
                      onChange={(value) => setFormData({ ...formData, name: value })}
                      portalled={false}
                      options={(expenseNamesList?.items || []).map((item) => ({
                        value: item.name,
                        label: item.name,
                      }))}
                      placeholder=""
                      emptyText="Nenhum nome encontrado"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      Tipo <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.expense_type}
                      onValueChange={expenseTypeGate.guardChange((value: ExpenseType) => setFormData({ ...formData, expense_type: value }))}
                    >
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="continuous">{expenseTypeGate.itemContent('continuous', 'Contínua')}</SelectItem>
                        <SelectItem value="installment">{expenseTypeGate.itemContent('installment', 'Parcelada')}</SelectItem>
                        <SelectItem value="single">{expenseTypeGate.itemContent('single', 'Única')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="total_installments">
                      Total de parcelas <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="total_installments"
                      type="number"
                      min="1"
                      placeholder="12"
                      value={formData.total_installments}
                      onChange={(e) => setFormData({ ...formData, total_installments: e.target.value })}
                      className={createFormTotalInstallmentError ? 'border-destructive' : ''}
                      aria-invalid={!!createFormTotalInstallmentError}
                      aria-describedby={createFormTotalInstallmentError ? 'total-installments-error' : undefined}
                    />
                    {createFormTotalInstallmentError && (
                      <p id="total-installments-error" className="text-sm text-destructive">
                        {createFormTotalInstallmentError}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="remaining_installments">
                      Parcelas restantes <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="remaining_installments"
                      type="number"
                      min="1"
                      placeholder="12"
                      value={formData.remaining_installments}
                      onChange={(e) => setFormData({ ...formData, remaining_installments: e.target.value })}
                      className={createFormRemainingInstallmentError ? 'border-destructive' : ''}
                      aria-invalid={!!createFormRemainingInstallmentError}
                      aria-describedby={
                        createFormRemainingInstallmentError ? 'remaining-installments-error' : undefined
                      }
                    />
                    {createFormRemainingInstallmentError && (
                      <p id="remaining-installments-error" className="text-sm text-destructive">
                        {createFormRemainingInstallmentError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="amount">
                      Valor mensal a pagar <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="amount"
                      value={formData.planned_amount || 0}
                      onChange={(value) => setFormData({ ...formData, planned_amount: value.toString() })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="due_date">
                      Data <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <DateInput
                      id="due_date"
                      value={formData.due_date}
                      onChange={(value) => setFormData({ ...formData, due_date: value })}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="category">
                      Categoria <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriesList?.items?.map((item) => (
                          <SelectItem key={item.id} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="monthly-expense-agent">
                      Agente da despesa <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.agent_id}
                      onValueChange={(value) => setFormData({ ...formData, agent_id: value })}
                    >
                      <SelectTrigger id="monthly-expense-agent" disabled={!agents?.length}>
                        <SelectValue placeholder="Selecione o agente" />
                      </SelectTrigger>
                      <SelectContent>
                        {expenseAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    placeholder="Escreva aqui..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">
                    Nome da despesa <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    id="name"
                    mode="input"
                    value={formData.name}
                    onChange={(value) => setFormData({ ...formData, name: value })}
                    portalled={false}
                    options={(expenseNamesList?.items || []).map((item) => ({
                      value: item.name,
                      label: item.name,
                    }))}
                    placeholder=""
                    emptyText="Nenhum nome encontrado"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      Tipo <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.expense_type}
                      onValueChange={expenseTypeGate.guardChange((value: ExpenseType) => setFormData({ ...formData, expense_type: value }))}
                    >
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="continuous">{expenseTypeGate.itemContent('continuous', 'Contínua')}</SelectItem>
                        <SelectItem value="installment">{expenseTypeGate.itemContent('installment', 'Parcelada')}</SelectItem>
                        <SelectItem value="single">{expenseTypeGate.itemContent('single', 'Única')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="amount">
                      Valor a pagar <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="amount"
                      value={formData.planned_amount || 0}
                      onChange={(value) => setFormData({ ...formData, planned_amount: value.toString() })}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="category">
                    Categoria <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriesList?.items?.map((item) => (
                        <SelectItem key={item.id} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="due_date">
                    Data <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <DateInput
                    id="due_date"
                    value={formData.due_date}
                    onChange={(value) => setFormData({ ...formData, due_date: value })}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="monthly-expense-agent">
                    Agente da despesa <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.agent_id}
                    onValueChange={(value) => setFormData({ ...formData, agent_id: value })}
                  >
                    <SelectTrigger id="monthly-expense-agent" disabled={!agents?.length}>
                      <SelectValue placeholder="Selecione o agente" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    placeholder="Escreva aqui..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.monthlySave}
              // Formulário completo fica verde: sinaliza que já pode criar.
              className={isCreateFormComplete && !isCreating ? CREATE_READY_BUTTON_CLASS : undefined}
              disabled={isCreating || !isCreateFormComplete}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar Despesa'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Despesa Mensal</DialogTitle>
            <DialogDescription>
              Altere os dados da despesa "{expenseToEdit?.name}".
            </DialogDescription>
          </DialogHeader>
          {expenseToEdit?.is_balance_carryover && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 mt-1">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs leading-snug text-amber-800 dark:text-amber-300">
                Atenção: alterar este valor influencia o saldo do próximo período orçamentário.
                Por ser o Saldo do PO anterior, qualquer nova alteração no período anterior
                substituirá sua edição.
              </p>
            </div>
          )}
          <div className="grid gap-4 py-4 px-1">
            {editFormData.expense_type === 'installment' ? (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_name">
                      Nome da despesa <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      id="edit_name"
                      mode="input"
                      value={editFormData.name}
                      onChange={(value) => setEditFormData({ ...editFormData, name: value })}
                      portalled={false}
                      options={(expenseNamesList?.items || []).map((item) => ({
                        value: item.name,
                        label: item.name,
                      }))}
                      placeholder=""
                      emptyText="Nenhum nome encontrado"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_type">
                      Tipo <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={editFormData.expense_type}
                      onValueChange={expenseTypeGate.guardChange((value: ExpenseType) =>
                        setEditFormData({ ...editFormData, expense_type: value })
                      )}
                    >
                      <SelectTrigger id="edit_type">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="continuous">{expenseTypeGate.itemContent('continuous', 'Contínua')}</SelectItem>
                        <SelectItem value="installment">{expenseTypeGate.itemContent('installment', 'Parcelada')}</SelectItem>
                        <SelectItem value="single">{expenseTypeGate.itemContent('single', 'Única')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_total_installments">
                      Total de parcelas <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit_total_installments"
                      type="number"
                      min="1"
                      placeholder="12"
                      value={editFormData.total_installments}
                      onChange={(e) => setEditFormData({ ...editFormData, total_installments: e.target.value })}
                      className={editFormTotalInstallmentError ? 'border-destructive' : ''}
                      aria-invalid={!!editFormTotalInstallmentError}
                      aria-describedby={editFormTotalInstallmentError ? 'edit-total-installments-error' : undefined}
                    />
                    {editFormTotalInstallmentError && (
                      <p id="edit-total-installments-error" className="text-sm text-destructive">
                        {editFormTotalInstallmentError}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_remaining_installments">
                      Parcelas restantes <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit_remaining_installments"
                      type="number"
                      min="1"
                      placeholder="12"
                      value={editFormData.remaining_installments}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, remaining_installments: e.target.value })
                      }
                      className={editFormRemainingInstallmentError ? 'border-destructive' : ''}
                      aria-invalid={!!editFormRemainingInstallmentError}
                      aria-describedby={
                        editFormRemainingInstallmentError ? 'edit-remaining-installments-error' : undefined
                      }
                    />
                    {editFormRemainingInstallmentError && (
                      <p id="edit-remaining-installments-error" className="text-sm text-destructive">
                        {editFormRemainingInstallmentError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_amount">
                      Valor mensal a pagar <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="edit_amount"
                      value={editFormData.planned_amount || 0}
                      onChange={(value) =>
                        setEditFormData({ ...editFormData, planned_amount: value.toString() })
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_due_date">
                      Data <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <DateInput
                      id="edit_due_date"
                      value={editFormData.due_date}
                      onChange={(value) => setEditFormData({ ...editFormData, due_date: value })}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_category">
                      Categoria <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={editFormData.category}
                      onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
                    >
                      <SelectTrigger id="edit_category">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriesList?.items?.map((item) => (
                          <SelectItem key={item.id} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-monthly-expense-agent">
                      Agente da despesa <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={editFormData.agent_id}
                      onValueChange={(value) => setEditFormData({ ...editFormData, agent_id: value })}
                    >
                      <SelectTrigger id="edit-monthly-expense-agent" disabled={!agents?.length}>
                        <SelectValue placeholder="Selecione o agente" />
                      </SelectTrigger>
                      <SelectContent>
                        {expenseAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_notes">Observações</Label>
                  <Textarea
                    id="edit_notes"
                    placeholder="Escreva aqui..."
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="edit_name">
                    Nome da despesa <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    id="edit_name"
                    mode="input"
                    value={editFormData.name}
                    onChange={(value) => setEditFormData({ ...editFormData, name: value })}
                    portalled={false}
                    options={(expenseNamesList?.items || []).map((item) => ({
                      value: item.name,
                      label: item.name,
                    }))}
                    placeholder=""
                    emptyText="Nenhum nome encontrado"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_type">
                      Tipo <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={editFormData.expense_type}
                      onValueChange={expenseTypeGate.guardChange((value: ExpenseType) =>
                        setEditFormData({ ...editFormData, expense_type: value })
                      )}
                    >
                      <SelectTrigger id="edit_type">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="continuous">{expenseTypeGate.itemContent('continuous', 'Contínua')}</SelectItem>
                        <SelectItem value="installment">{expenseTypeGate.itemContent('installment', 'Parcelada')}</SelectItem>
                        <SelectItem value="single">{expenseTypeGate.itemContent('single', 'Única')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_amount">
                      Valor a pagar <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="edit_amount"
                      value={editFormData.planned_amount || 0}
                      onChange={(value) =>
                        setEditFormData({ ...editFormData, planned_amount: value.toString() })
                      }
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_category">
                    Categoria <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={editFormData.category}
                    onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
                  >
                    <SelectTrigger id="edit_category">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriesList?.items?.map((item) => (
                        <SelectItem key={item.id} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_due_date">
                    Data <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <DateInput
                    id="edit_due_date"
                    value={editFormData.due_date}
                    onChange={(value) => setEditFormData({ ...editFormData, due_date: value })}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-monthly-expense-agent">
                    Agente da despesa <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={editFormData.agent_id}
                    onValueChange={(value) => setEditFormData({ ...editFormData, agent_id: value })}
                  >
                    <SelectTrigger id="edit-monthly-expense-agent" disabled={!agents?.length}>
                      <SelectValue placeholder="Selecione o agente" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_notes">Observações</Label>
                  <Textarea
                    id="edit_notes"
                    placeholder="Escreva aqui..."
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAttemptUpdateExpense}
              disabled={
                isUpdating ||
                !editFormData.name ||
                !editFormData.planned_amount ||
                !editFormData.due_date ||
                !editFormData.category ||
                !editFormData.agent_id ||
                (editFormData.expense_type === 'installment' &&
                  (!editFormData.remaining_installments || !editFormData.total_installments)) ||
                !!editFormTotalInstallmentError ||
                !!editFormRemainingInstallmentError
              }
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scope Dialog (Contínua/Parcelada) */}
      <Dialog
        open={isEditScopeDialogOpen}
        onOpenChange={(open) => {
          if (!isUpdating) setIsEditScopeDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar {expenseToEdit?.name}</DialogTitle>
            <DialogDescription>De que forma deseja editar esta despesa?</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <RadioGroup
              value={editScope}
              onValueChange={(value) => setEditScope(value as 'current' | 'future')}
              className="space-y-3"
              disabled={isUpdating}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="current" id="edit-scope-current" />
                <Label htmlFor="edit-scope-current" className="font-normal">
                  Somente para este PO
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="future" id="edit-scope-future" />
                <Label htmlFor="edit-scope-future" className="font-normal">
                  Para este PO e os POs seguintes
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground mt-3">
              As edições não poderão ter efeitos retroativos.
            </p>
            {editFormData.expense_type === 'installment' &&
              Number.parseInt(editFormData.remaining_installments, 10) === 1 && (
                <p className="text-xs text-destructive mt-2" role="alert">
                  Atenção: esta despesa está na última parcela (1 restante). Salvar
                  para este PO e os POs seguintes encerra a série e remove as
                  parcelas pendentes dos POs futuros.
                </p>
              )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditScopeDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmEditScope} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type-Change Dialog (informa que a mudança de tipo afeta este PO e os seguintes) */}
      <Dialog
        open={isTypeChangeDialogOpen}
        onOpenChange={(open) => {
          if (!isUpdating) setIsTypeChangeDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Alterar tipo de {expenseToEdit?.name}</DialogTitle>
            <DialogDescription>
              Ao alterar o tipo da despesa, a mudança será aplicada no PO atual e nos POs seguintes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-muted-foreground">*Esta ação não poderá ser desfeita</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTypeChangeDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmTypeChange} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir despesas selecionadas</DialogTitle>
            <DialogDescription>
              Esta ação excluirá todas as despesas contínuas e parceladas dos PO&apos;s futuros. Deseja confirmar a exclusão?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)} disabled={isBulkDeleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteExpenses}
              disabled={isBulkDeleting || selectedExpenseCount === 0}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog (Única) */}
      <Dialog open={isDeleteConfirmDialogOpen} onOpenChange={setIsDeleteConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Excluir {expenseToDelete?.name}</DialogTitle>
            <DialogDescription>Tem certeza de que deseja excluir?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteConfirmDialogOpen(false);
                setExpenseToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleConfirmDelete(false)}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Scope Dialog (Contínua/Parcelada) */}
      <Dialog open={isDeleteScopeDialogOpen} onOpenChange={setIsDeleteScopeDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Excluir {expenseToDelete?.name}</DialogTitle>
            <DialogDescription>De que forma deseja excluir esta despesa?</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <RadioGroup
              value={deleteScope}
              onValueChange={(value) => setDeleteScope(value as 'current' | 'future')}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="current" id="delete-scope-current" />
                <Label htmlFor="delete-scope-current" className="font-normal">
                  Somente para este mês
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="future" id="delete-scope-future" />
                <Label htmlFor="delete-scope-future" className="font-normal">
                  Para este mês e registros futuros
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground mt-3">
              *Esta ação não poderá ser desfeita
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteScopeDialogOpen(false);
                setExpenseToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={() => handleConfirmDelete(deleteScope === 'future')}>Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Realize Expense Dialog */}
      <Dialog open={isRealizeDialogOpen} onOpenChange={setIsRealizeDialogOpen}>
        <DialogContent data-tour-id="monthly-realize-dialog" className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Realizar Despesa</DialogTitle>
            <DialogDescription>
              Informe os dados do pagamento para "{selectedExpense?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Valor planejado</Label>
                <CurrencyDisplay value={selectedExpense?.planned_amount || 0} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="realized_amount">Valor pago (R$)</Label>
                <CurrencyInput
                  id="realized_amount"
                  value={realizeFormData.realized_amount || 0}
                  onChange={(value) =>
                    setRealizeFormData({ ...realizeFormData, realized_amount: value.toString() })
                  }
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realized_date">Data de pagamento</Label>
                <DateInput
                  id="realized_date"
                  value={realizeFormData.realized_date}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, realized_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="agent_id">Quem pagou</Label>
                <Select 
                  value={realizeFormData.agent_id} 
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, agent_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {payerAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment_method">Forma de pagamento</Label>
              <Select
                value={realizeFormData.payment_method}
                onValueChange={(value) =>
                  setRealizeFormData({ ...realizeFormData, payment_method: value })
                }
              >
                <SelectTrigger aria-label="Forma de pagamento">
                  <SelectValue placeholder="Selecione a forma de pagamento" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {paymentMethodsList?.items?.map((method) => (
                    <SelectItem key={method.id} value={method.name}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="realize_notes">Observações (opcional)</Label>
              <Textarea
                id="realize_notes"
                placeholder="Notas adicionais..."
                value={realizeFormData.notes}
                onChange={(e) => setRealizeFormData({ ...realizeFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRealizeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRealizeExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.monthlyConfirm}
              disabled={
                isRealizing ||
                !realizeFormData.realized_amount ||
                !realizeFormData.realized_date ||
                !realizeFormData.agent_id ||
                !realizeFormData.payment_method
              }
            >
              {isRealizing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Pagando...
                </>
              ) : (
                'Pagar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Realized Expense Dialog */}
      <Dialog open={isEditRealizedDialogOpen} onOpenChange={setIsEditRealizedDialogOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Editar Realizado{selectedExpense?.name ? ` - ${selectedExpense.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Edite os dados do pagamento. Campos com * são obrigatórios.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_amount">
                  Valor pago <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <CurrencyInput
                  id="edit_realized_amount"
                  value={Number.parseFloat(editRealizedFormData.realized_amount) || 0}
                  onChange={(value) =>
                    setEditRealizedFormData({ ...editRealizedFormData, realized_amount: value.toString() })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_date">
                  Data de pagamento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <DateInput
                  id="edit_realized_date"
                  value={editRealizedFormData.realized_date}
                  onChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, realized_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_agent_id">
                  Quem pagou <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select 
                  value={editRealizedFormData.agent_id} 
                  onValueChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, agent_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {payerAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_payment_method">
                  Forma de pagamento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select
                  value={editRealizedFormData.payment_method}
                  onValueChange={(value) =>
                    setEditRealizedFormData({ ...editRealizedFormData, payment_method: value })
                  }
                >
                  <SelectTrigger aria-label="Forma de pagamento">
                    <SelectValue placeholder="Selecione a forma de pagamento" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {paymentMethodsList?.items?.map((method) => (
                      <SelectItem key={method.id} value={method.name}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => setIsUndoRealizedDialogOpen(true)}
              disabled={isUndoingRealization}
              title="Excluir realizado"
              aria-label="Excluir realizado"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive sm:mr-auto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => setIsEditRealizedDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateRealized}
              disabled={
                isUpdating ||
                !editRealizedFormData.realized_amount ||
                !editRealizedFormData.realized_date ||
                !editRealizedFormData.agent_id ||
                !editRealizedFormData.payment_method
              }
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo Realized Expense Confirmation Dialog */}
      <Dialog open={isUndoRealizedDialogOpen} onOpenChange={setIsUndoRealizedDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              Excluir realizado{selectedExpense?.name ? ` - ${selectedExpense.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja excluir o realizado? Os dados do pagamento serão removidos e
              a despesa voltará para planejado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsUndoRealizedDialogOpen(false)}
              disabled={isUndoingRealization}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmUndoRealized}
              disabled={isUndoingRealization}
            >
              {isUndoingRealization ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderExpensesList(
  expenses: MonthlyExpense[],
  onDelete: (expense: MonthlyExpense) => void,
  onRealize: (expense: MonthlyExpense) => void,
  onEdit: (expense: MonthlyExpense) => void,
  onEditRealized: (expense: MonthlyExpense) => void,
  onCreate: () => void,
  canPlanInPeriod: boolean,
  canRealizeInPeriod: boolean,
  selectedExpenseIds: string[],
  onToggleSelection: (expenseId: string, checked: boolean) => void
) {
  if (expenses.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Receipt className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-medium mb-1">Nenhuma despesa mensal cadastrada</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
            Cadastre suas obrigações mensais como aluguel, condomínio, seguros, streaming, etc.
          </p>
          <Button onClick={onCreate} className="gap-2" disabled={!canPlanInPeriod}>
            <Plus className="h-4 w-4" />
            Nova Despesa
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Lançamento que o usuário criou durante o tutorial: recebe o destaque do tour.
  const tutorialExpenseId = findTutorialCreatedItem(TUTORIAL_SCOPE, expenses)?.id;

  return expenses.map((expense) => {
    const isSelected = selectedExpenseIds.includes(expense.id);

    return (
      <div key={expense.id} className="relative">
        {canPlanInPeriod && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onToggleSelection(expense.id, checked === true)}
            aria-label={`Selecionar ${expense.name}`}
            className="absolute left-4 top-4 z-10"
          />
        )}
        <TransactionCard
            id={expense.id}
            name={expense.name}
            type="expense"
            subtype={expense.expense_type === 'installment'
              ? expense.current_installment && expense.total_installments
                ? `Parcelada ${expense.current_installment}/${expense.total_installments}`
                : 'Parcelada'
              : expense.expense_type === 'continuous'
              ? 'Contínua'
              : 'Única'
            }
            plannedAmount={getExpensePlannedAmountForPO(expense)}
            realizedAmount={expense.realized_amount}
            status={expense.status}
            isBalanceCarryover={expense.is_balance_carryover}
            category={expense.category}
            dueDate={expense.due_date}
            realizedDate={expense.realized_date}
            isOverdue={expense.is_overdue}
            icon={Receipt}
            onRealize={expense.status === 'pending' ? () => onRealize(expense) : undefined}
            realizeTourId={
              tutorialExpenseId === expense.id
                ? FIRST_ACCESS_TUTORIAL_STEPS.monthlyPay
                : undefined
            }
            disableRealize={!canRealizeInPeriod}
            onEdit={canPlanInPeriod ? () => onEdit(expense) : undefined}
            onEditRealized={expense.status === 'realized' ? () => onEditRealized(expense) : undefined}
            onDelete={canPlanInPeriod ? () => onDelete(expense) : undefined}
            className={canPlanInPeriod ? 'pl-10 sm:pl-12' : undefined}
          />
      </div>
    );
  });
}

export default MonthlyExpensesPage;
